import type {
  Invoice,
  TrialKind,
  PlanLimits,
  PlanWarning,
  BillingState,
  UsageSummary,
  PaymentMethod,
  CreditBalance,
  CreditPricing,
  ActivePlanGrant,
  RawSubscription,
  BillingInterval,
  PackEnabledFeature,
  SubscriptionWarning,
  CreditFeaturePricing,
} from 'src/sections/settings/billing/types/billing';

// ----------------------------------------------------------------------
// Field readers — the API serializes camelCase but PascalCase has been seen from older billing
// endpoints, so every field is accepted either way. Exported because the billing feature's own
// utils normalize coupon payloads the same way; two implementations of "how we read this API"
// would be one too many.
// ----------------------------------------------------------------------

const pascalOf = (key: string) => key.charAt(0).toUpperCase() + key.slice(1);

export function readString(o: Record<string, unknown>, key: string): string | null {
  const v = o[key] ?? o[pascalOf(key)];
  return v == null ? null : String(v);
}

export function readNumber(o: Record<string, unknown>, key: string, fallback = 0): number {
  const v = o[key] ?? o[pascalOf(key)];
  if (v == null) return fallback;
  const n = Number(v);
  // A non-numeric value would otherwise render as "NaN credits".
  return Number.isFinite(n) ? n : fallback;
}

export function readBool(o: Record<string, unknown>, key: string, fallback = false): boolean {
  const v = o[key] ?? o[pascalOf(key)];
  if (v == null) return fallback;
  // Guard the string case explicitly: Boolean('false') is true.
  if (typeof v === 'string') return v.toLowerCase() === 'true';
  return Boolean(v);
}

// ----------------------------------------------------------------------

export function normalizeSubscription(raw: Record<string, unknown>): RawSubscription {
  return {
    planSlug: (raw.planSlug || raw.PlanSlug) as string | undefined,
    status: (raw.status || raw.Status) as string | undefined,
    currentPeriodStart: (raw.currentPeriodStart ||
      raw.CurrentPeriodStart ||
      raw.dateCurrentPeriodStart ||
      raw.DateCurrentPeriodStart) as string | undefined,
    currentPeriodEnd: (raw.currentPeriodEnd ||
      raw.CurrentPeriodEnd ||
      raw.dateCurrentPeriodEnd ||
      raw.DateCurrentPeriodEnd) as string | undefined,
    cancelAtPeriodEnd: (raw.cancelAtPeriodEnd ?? raw.CancelAtPeriodEnd) as boolean | undefined,
    dateCanceled: (raw.dateCanceled ||
      raw.DateCanceled ||
      raw.cancelledAt ||
      raw.CancelledAt) as string | undefined,
    isInTrial: Boolean(raw.isInTrial ?? raw.IsInTrial ?? false),
    trialStart: ((raw.trialStart || raw.TrialStart || raw.trialStartDate || raw.TrialStartDate) as string | undefined) ?? null,
    trialEnd: ((raw.trialEnd || raw.TrialEnd || raw.trialEndDate || raw.TrialEndDate) as string | undefined) ?? null,
    trialSource: normalizeTrialKind(raw.trialSource ?? raw.TrialSource),
    billingInterval: normalizeBillingInterval(raw.billingInterval ?? raw.BillingInterval),
    unitAmountCents: normalizeCents(raw.unitAmountCents ?? raw.UnitAmountCents),
  };
}

/** Only the two cadences the catalog can hold; anything else reads as unknown. */
function normalizeBillingInterval(value: unknown): BillingInterval | null {
  const s = typeof value === 'string' ? value.toLowerCase() : null;
  return s === 'month' || s === 'year' ? s : null;
}

function normalizeCents(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Accepts only the three known trial provenances; anything else reads as unknown. */
function normalizeTrialKind(value: unknown): TrialKind | null {
  const s = typeof value === 'string' ? value.toLowerCase() : null;
  return s === 'events' || s === 'coupon' || s === 'staff' ? s : null;
}

function mapInvoiceStatus(apiStatus: string): Invoice['status'] {
  const s = apiStatus.toLowerCase();
  if (s === 'paid') return 'paid';
  if (s === 'payment_failed' || s === 'failed' || s === 'uncollectible') return 'failed';
  if (s === 'open' || s === 'pending' || s === 'draft') return 'pending';
  if (s === 'void' || s === 'refunded') return 'refunded';
  return 'paid';
}

export function deriveBillingState(
  currentPlan: string | null,
  subscription: RawSubscription | null,
  optimisticCancelAtPeriodEnd: boolean
): BillingState {
  void currentPlan;
  if (subscription) {
    const subStatus = (subscription.status || '').toLowerCase();
    const cancelAtPeriodEnd =
      optimisticCancelAtPeriodEnd || (subscription.cancelAtPeriodEnd ?? false);

    if (subStatus === 'canceled' || subStatus === 'cancelled') return 'expired';
    if (subStatus === 'incomplete_expired') return 'expired';
    if (cancelAtPeriodEnd) return 'cancelled';
    if (subStatus === 'active' || subStatus === 'trialing') return 'active';
    if (subStatus === 'past_due') return 'active';
    if (subStatus === 'unpaid') return 'active';
    if (subStatus === 'incomplete') return 'active';
    if (subStatus === 'paused') return 'active';
  }

  return 'free';
}

export function deriveSubscriptionWarning(
  subscription: RawSubscription | null,
  paymentMethod: PaymentMethod | null
): SubscriptionWarning {
  if (subscription) {
    const subStatus = (subscription.status || '').toLowerCase();
    if (subStatus === 'past_due') return 'past_due';
    if (subStatus === 'unpaid') return 'unpaid';
    if (subStatus === 'incomplete') return 'incomplete';
    if (subStatus === 'incomplete_expired') return 'incomplete_expired';
    if (subStatus === 'paused') return 'paused';
    if (subStatus === 'trialing') return 'trialing';
  }

  if (paymentMethod && paymentMethod.expMonth && paymentMethod.expYear) {
    const now = new Date();
    const expDate = new Date(paymentMethod.expYear, paymentMethod.expMonth);
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    if (expDate.getTime() - now.getTime() < thirtyDays) return 'card_expiring';
  }

  return null;
}

export function normalizeInvoices(data: unknown): Invoice[] | null {
  if (!data) return null;
  const raw = data as Record<string, unknown>;
  const arr = Array.isArray(raw) ? raw : raw.invoices || raw.Invoices;
  if (!Array.isArray(arr)) return null;
  return arr.map((inv: Record<string, unknown>, idx: number) => ({
    id: String(inv.id || inv.Id || idx),
    date: String(inv.date || inv.Date || inv.createdAt || inv.CreatedAt || ''),
    total: Number(inv.total || inv.Total || inv.amountPaid || inv.AmountPaid || 0),
    currency: String(inv.currency || inv.Currency || 'usd'),
    status: mapInvoiceStatus(String(inv.status || inv.Status || 'paid')),
    viewUrl:
      String(
        inv.invoiceUrl || inv.InvoiceUrl || inv.hostedInvoiceUrl || inv.HostedInvoiceUrl || ''
      ) || undefined,
    hostedInvoiceUrl: String(inv.hostedInvoiceUrl || inv.HostedInvoiceUrl || '') || undefined,
  }));
}

export function normalizePaymentMethod(data: unknown): PaymentMethod | null {
  if (!data) return null;
  const raw = data as Record<string, unknown>;
  const pm = (raw.paymentMethod || raw.PaymentMethod) as Record<string, unknown> | null;
  if (!pm) return null;
  return {
    brand: String(pm.brand || pm.Brand || ''),
    last4: String(pm.last4 || pm.Last4 || ''),
    expMonth: Number(pm.expMonth || pm.ExpMonth || 0),
    expYear: Number(pm.expYear || pm.ExpYear || 0),
  };
}

export function normalizePlanLimits(data: unknown): PlanLimits | null {
  if (!data) return null;
  const raw = data as Record<string, unknown>;
  const limits = (raw.planLimits || raw.PlanLimits) as Record<string, unknown> | null;
  if (!limits) return null;
  return {
    maxCandidates: Number(limits.maxCandidates ?? limits.MaxCandidates ?? 0),
    maxScreenings: Number(limits.maxScreenings ?? limits.MaxScreenings ?? 0),
    maxInterviews: Number(limits.maxInterviews ?? limits.MaxInterviews ?? 0),
    maxCodingRounds: Number(limits.maxCodingRounds ?? limits.MaxCodingRounds ?? 0),
    maxJobPosts: Number(limits.maxJobPosts ?? limits.MaxJobPosts ?? limits.maxJobs ?? limits.MaxJobs ?? 0),
    hasAiInterviews: Boolean(limits.hasAiInterviews ?? limits.HasAiInterviews ?? false),
    hasCustomBranding: Boolean(limits.hasCustomBranding ?? limits.HasCustomBranding ?? false),
    hasRemoveWatermark: Boolean(limits.hasRemoveWatermark ?? limits.HasRemoveWatermark ?? false),
    hasAutomations: Boolean(limits.hasAutomations ?? limits.HasAutomations ?? false),
    hasReports: Boolean(limits.hasReports ?? limits.HasReports ?? false),
    hasIntegrations: Boolean(limits.hasIntegrations ?? limits.HasIntegrations ?? false),
    allowedAiAgents:
      ((limits.allowedAiAgents ?? limits.AllowedAiAgents) as string[] | undefined) ?? [],
    allowedDurations:
      ((limits.allowedDurations ?? limits.AllowedDurations) as number[] | undefined) ?? [],
  };
}

export function normalizeUsageSummary(data: unknown): UsageSummary | null {
  if (!data) return null;
  const raw = data as Record<string, unknown>;
  const usage = (raw.currentUsage || raw.CurrentUsage) as Record<string, unknown> | null;
  if (!usage) return null;
  return {
    candidates: Number(usage.candidates ?? usage.Candidates ?? 0),
    screenings: Number(usage.screenings ?? usage.Screenings ?? 0),
    interviews: Number(usage.interviews ?? usage.Interviews ?? 0),
    codingRounds: Number(usage.codingRounds ?? usage.CodingRounds ?? 0),
    jobPosts: Number(usage.jobPosts ?? usage.JobPosts ?? usage.jobs ?? usage.Jobs ?? 0),
    periodStart: String(usage.periodStart || usage.PeriodStart || ''),
    periodEnd: String(usage.periodEnd || usage.PeriodEnd || ''),
  };
}

export function normalizePlanWarnings(data: unknown): PlanWarning[] | null {
  if (!data) return null;
  const raw = data as Record<string, unknown>;
  const arr = (raw.warnings || raw.Warnings) as unknown[] | null;
  if (!Array.isArray(arr) || arr.length === 0) return null;
  return arr.map((w: unknown) => {
    const item = w as Record<string, unknown>;
    return {
      feature: String(item.feature || item.Feature || ''),
      featureLabel: String(item.featureLabel || item.FeatureLabel || ''),
      used: Number(item.used ?? item.Used ?? 0),
      limit: Number(item.limit ?? item.Limit ?? 0),
      percentage: Number(item.percentage ?? item.Percentage ?? 0),
      message: String(item.message || item.Message || ''),
    };
  });
}

export function normalizeCurrentPlan(data: unknown): string | null {
  if (!data) return null;
  const raw = data as Record<string, unknown>;
  const org = (raw.organization || raw.Organization) as Record<string, unknown> | undefined;
  return String(org?.currentPlan || org?.CurrentPlan || '') || null;
}

export function normalizeIsDevAccount(data: unknown): boolean {
  if (!data) return false;
  const raw = data as Record<string, unknown>;
  const org = (raw.organization || raw.Organization) as Record<string, unknown> | undefined;
  return Boolean(org?.isDevAccount ?? org?.IsDevAccount ?? false);
}

export function normalizeCreditBalances(data: unknown): CreditBalance[] {
  if (!data) return [];
  const raw = data as Record<string, unknown>;
  const arr = (raw.creditBalances || raw.CreditBalances) as unknown[] | null;
  if (!Array.isArray(arr)) return [];
  return arr.map((item: unknown) => {
    const b = item as Record<string, unknown>;
    const nextExpiry = b.nextExpiryAt ?? b.NextExpiryAt ?? null;
    const quantityExpiring = b.quantityExpiring ?? b.QuantityExpiring ?? null;
    return {
      feature: String(b.feature || b.Feature || ''),
      remaining: Number(b.remaining ?? b.Remaining ?? 0),
      lifetimeGranted: Number(b.lifetimeGranted ?? b.LifetimeGranted ?? 0),
      lifetimeConsumed: Number(b.lifetimeConsumed ?? b.LifetimeConsumed ?? 0),
      updatedAt: String(b.updatedAt || b.UpdatedAt || ''),
      nextExpiryAt: nextExpiry == null ? null : String(nextExpiry),
      quantityExpiring: quantityExpiring == null ? null : Number(quantityExpiring),
    };
  });
}

function normalizeFeaturePricing(
  data: unknown,
  feature: PackEnabledFeature
): CreditFeaturePricing | null {
  if (!data || typeof data !== 'object') return null;
  const p = data as Record<string, unknown>;
  const cents = p.pricePerUnitUsdCents ?? p.PricePerUnitUsdCents;
  if (cents == null) return null;
  return {
    feature,
    pricePerUnitUsdCents: Number(cents),
    displayName: String(p.displayName ?? p.DisplayName ?? ''),
    subtitle: String(p.subtitle ?? p.Subtitle ?? ''),
    unitLabel: String(p.unitLabel ?? p.UnitLabel ?? ''),
  };
}

export function normalizeCreditPricing(data: unknown): CreditPricing | null {
  if (!data) return null;
  const raw = data as Record<string, unknown>;
  const pricing = (raw.creditPricing || raw.CreditPricing) as Record<string, unknown> | null;
  if (!pricing) return null;
  const screening = normalizeFeaturePricing(
    pricing.screening ?? pricing.Screening,
    'screenings'
  );
  const interviews = normalizeFeaturePricing(
    pricing.interviews ?? pricing.Interviews,
    'interviews'
  );
  return {
    screening,
    interviews,
    purchaseIncrementUnits: Number(
      pricing.purchaseIncrementUnits ?? pricing.PurchaseIncrementUnits ?? 10
    ),
    minPurchaseUnits: Number(pricing.minPurchaseUnits ?? pricing.MinPurchaseUnits ?? 0),
    maxPurchaseUnits: Number(pricing.maxPurchaseUnits ?? pricing.MaxPurchaseUnits ?? 500),
    currencyCode: String(pricing.currencyCode ?? pricing.CurrencyCode ?? 'usd'),
  };
}

/**
 * The free-months grant currently running, from org-status. Null when there is none — which is
 * the normal case for a paying customer.
 */
export function normalizeActivePlanGrant(data: unknown): ActivePlanGrant | null {
  if (!data) return null;
  const raw = data as Record<string, unknown>;
  const grant = (raw.planGrant ?? raw.PlanGrant) as Record<string, unknown> | null | undefined;
  if (!grant) return null;

  const freeMonthsEnd = grant.freeMonthsEnd ?? grant.FreeMonthsEnd;
  if (!freeMonthsEnd) return null;

  return {
    kind: String(grant.kind ?? grant.Kind ?? ''),
    months: Number(grant.months ?? grant.Months ?? 0),
    couponCode: (grant.couponCode ?? grant.CouponCode ?? null) as string | null,
    freeMonthsEnd: String(freeMonthsEnd),
    requiresCardBeforeEnd: Boolean(grant.requiresCardBeforeEnd ?? grant.RequiresCardBeforeEnd ?? false),
    paidThrough: (grant.paidThrough ?? grant.PaidThrough ?? null) as string | null,
  };
}

/** Whether the org may buy credit top-ups right now. Computed server-side. */
export function normalizeCanBuyPack(data: unknown): boolean {
  if (!data) return false;
  const raw = data as Record<string, unknown>;
  return Boolean(raw.canBuyPack ?? raw.CanBuyPack ?? false);
}

